<template>
  <v-container v-if="fund && (mayEditMinimalRequirements || awaitingAudit)" fluid>
    <v-app-bar app color="secondary" dark dense>
      <v-toolbar-title>
        {{ fund.name }}
      </v-toolbar-title>
    </v-app-bar>

    <MinimalSuccess v-if="awaitingAudit" :fund="fund" />

    <template v-else>
      <v-row>
        <v-col cols="12">
          <DataTable :fund="fund" />
        </v-col>
      </v-row>

      <v-row v-if="!fund.precheck">
        <v-col cols="12">
          <h2 class="text-h5 mb-4">
            {{ $t("user.minimalRequirements.title") }}
          </h2>
        </v-col>
      </v-row>

      <v-row v-for="(category, index) in categories" :key="index">
        <v-col cols="12">
          <h3>
            {{ $t(`minimalRequirementCategory.${index}.name`) }}
          </h3>
        </v-col>

        <v-col v-for="requirement in category" :key="requirement.id" cols="12">
          <MinimalRequirementForm :fund="fund" :requirement="requirement" />
        </v-col>
      </v-row>

      <v-row v-if="!readyToFinish">
        <v-col cols="12">
          <v-alert type="error">
            {{ $t("user.minimalRequirements.notReadyToFinish") }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-btn color="secondary" class="mr-4" :to="{ name: 'home' }">
            {{ $t("user.minimalRequirements.back") }}
          </v-btn>

          <v-dialog v-model="dialog" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-btn :disabled="!readyToFinish" v-bind="attrs" v-on="on" color="primary" class="mr-4">
                {{ $t("user.minimalRequirements.finish") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="headline">
                {{
                  fund.precheck
                    ? $t("user.precheck.confirmation.title")
                    : $t("user.minimalRequirements.confirmation.title")
                }}
              </v-card-title>
              <v-card-text>
                <Markdown
                  :value="
                    fund.precheck
                      ? $t('user.precheck.confirmation.description')
                      : $t('user.minimalRequirements.confirmation.description')
                  "
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="warning" @click="dialog = false">
                  {{
                    fund.precheck
                      ? $t("user.precheck.confirmation.cancel")
                      : $t("user.minimalRequirements.confirmation.cancel")
                  }}
                </v-btn>
                <v-btn color="primary" @click="finish" :loading="loading">
                  {{
                    fund.precheck
                      ? $t("user.precheck.confirmation.accept")
                      : $t("user.minimalRequirements.confirmation.accept")
                  }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { all, includes } from "ramda";
import DataTable from "@/components/Fund/DataTable";
import MinimalSuccess from "@/components/User/Fund/MinimalSuccess";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import MinimalRequirementForm from "@/components/User/Fund/MinimalRequirementForm";
import mayEditMinimalRequirements from "@fng/common/mayEditMinimalRequirements";
import FundMinimalRequirementsQuery from "@/gql/queries/FundMinimalRequirementsQuery.gql";
import FinishEditMinimalRequirementsMutation from "@/gql/mutations/FinishEditMinimalRequirementsMutation.gql";

export default {
  components: {
    MinimalRequirementForm,
    MinimalSuccess,
    DataTable,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fund: null,
      dialog: false,
      loading: false,
    };
  },

  computed: {
    categories() {
      return groupByCategory(this.fund.minimalRequirements);
    },

    readyToFinish() {
      return all((req) => req.state === "DONE" || (req.state === "AUDIT" && !req.wip), this.fund.minimalRequirements);
    },

    awaitingAudit() {
      return includes(this.fund.minimalRequirementsState, ["AUDIT", "FINAL_AUDIT"]);
    },

    mayEditMinimalRequirements() {
      return mayEditMinimalRequirements(this.fund, this.fund.auditPeriod);
    },
  },

  apollo: {
    fund: {
      query: FundMinimalRequirementsQuery,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  methods: {
    async finish() {
      this.loading = true;

      await this.$apollo.mutate({
        mutation: FinishEditMinimalRequirementsMutation,
        variables: {
          id: this.fund.id,
        },
      });

      this.dialog = false;
      this.loading = false;
    },
  },
};
</script>
